<section class="app-page">
  <div class="px-4 md:px-12 py-6 space-y-6">
    <!-- Header -->
    <div class="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold">Sync watch</h1>
        <p class="text-base-content/60 text-sm max-w-3xl">
          Every trip on the road and whether its POS is getting its changes to the server. Checked every two
          minutes. Platform staff are alerted about a POS not heard from for
          {{ overview()?.silentAfterMinutes ?? 15 }} minutes, or online for
          {{ overview()?.stuckAfterMinutes ?? 10 }} minutes without getting a change through.
        </p>
      </div>
      <div class="flex items-center gap-2">
        <nz-select
          class="min-w-56"
          [ngModel]="companyId()"
          (ngModelChange)="selectTenant($event)"
          nzShowSearch
          nzPlaceHolder="All tenants"
        >
          <nz-option nzValue="" nzLabel="All tenants"></nz-option>
          <nz-option *ngFor="let t of tenants()" [nzValue]="t.id" [nzLabel]="t.name"></nz-option>
        </nz-select>
        <button type="button" class="btn btn-sm btn-ghost" (click)="refresh()" [disabled]="isRefreshing()">
          Refresh
        </button>
      </div>
    </div>

    <buspro-resources-loading-skeletons *ngIf="isInitialLoading()" [loading]="true"></buspro-resources-loading-skeletons>

    <div *ngIf="watchQuery.isError() && !overview()" role="alert" class="alert alert-error">
      <span>The sync watch could not be loaded. It refreshes on its own every 30 seconds.</span>
    </div>

    <nz-spin *ngIf="overview() as o" [nzSpinning]="isRefreshing()">
      <div class="space-y-6">
        <!-- Counts -->
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div class="rounded-box border border-base-300 bg-base-100 p-4">
            <div class="text-xs uppercase tracking-wide text-base-content/60">On the road</div>
            <div class="text-2xl font-semibold">{{ counts()?.onTheRoad ?? '–' }}</div>
          </div>
          <div class="rounded-box border border-error/30 bg-error/10 p-4">
            <div class="text-xs uppercase tracking-wide text-error">Not uploading</div>
            <div class="text-2xl font-semibold text-error">{{ counts()?.stuck ?? '–' }}</div>
          </div>
          <div class="rounded-box border border-warning/30 bg-warning/10 p-4">
            <div class="text-xs uppercase tracking-wide text-warning">Silent</div>
            <div class="text-2xl font-semibold text-warning">{{ counts()?.silent ?? '–' }}</div>
          </div>
          <div class="rounded-box border border-base-300 bg-base-100 p-4">
            <div class="text-xs uppercase tracking-wide text-base-content/60">Not reporting (older app)</div>
            <div class="text-2xl font-semibold">{{ counts()?.notReporting ?? '–' }}</div>
          </div>
        </div>

        <!-- Open alerts -->
        <div class="space-y-3">
          <h2 class="text-lg font-semibold">Open alerts</h2>
          <div
            *ngIf="openAlerts().length === 0"
            class="rounded-box border border-success/30 bg-success/10 px-4 py-3 text-sm text-success"
          >
            No open alerts. Every POS on the road is reporting and uploading.
          </div>
          <div
            *ngFor="let alert of openAlerts(); trackBy: trackAlert"
            class="rounded-box border border-base-300 bg-base-100 p-4 space-y-2"
          >
            <div class="flex flex-wrap items-center gap-2">
              <span [ngClass]="alertBadge(alert)">{{ alertLabel(alert) }}</span>
              <span class="font-semibold">{{ alert.busName || 'Unknown bus' }}</span>
              <span class="text-base-content/60 text-sm">{{ alert.companyName }} · {{ alert.routeName }}</span>
            </div>
            <p class="text-sm">{{ alert.summary }}</p>
            <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-base-content/60">
              <span>Opened {{ alert.openedAt | date: 'd MMM, HH:mm' }}</span>
              <span>Still true at {{ alert.lastSeenAt | date: 'HH:mm' }}</span>
              <a
                *ngIf="alert.conductorPhoneNumber"
                class="link link-primary"
                [href]="telHref(alert.conductorPhoneNumber)"
                >Call {{ alert.conductorName }} ({{ alert.conductorPhoneNumber }})</a
              >
            </div>
          </div>
        </div>

        <!-- Refused changes held on phones -->
        <div *ngIf="setAsideDevices().length > 0" class="space-y-3">
          <h2 class="text-lg font-semibold">Changes set aside on phones</h2>
          <p class="text-sm text-base-content/60 max-w-3xl">
            The server refused these changes, so the phone set them aside to let everything after them upload. They are
            <strong>not on the server</strong>. The phone does not retry them — a later change to the same record may
            already be here — so each needs a person to decide. The full record is on the phone's Sync Diagnostics
            screen.
          </p>
          <div
            *ngFor="let d of setAsideDevices(); trackBy: trackDevice"
            class="rounded-box border border-error/30 bg-base-100 p-4 space-y-2"
          >
            <div class="flex flex-wrap items-center gap-2">
              <span class="badge badge-error">{{ d.setAside }} set aside</span>
              <span class="font-semibold">{{ d.userName || 'Unknown user' }}</span>
              <span class="text-base-content/60 text-sm">{{ d.companyName }} · app {{ d.appVersion }}</span>
              <a *ngIf="d.userPhoneNumber" class="link link-primary text-sm" [href]="telHref(d.userPhoneNumber)">{{
                d.userPhoneNumber
              }}</a>
            </div>
            <ul class="text-sm space-y-1">
              <li *ngFor="let item of d.items" class="flex flex-col md:flex-row md:gap-2">
                <span class="font-mono text-xs text-base-content/60"
                  >{{ item.table }} {{ item.op }} {{ item.rowId }}</span
                >
                <span class="text-error">{{ item.error }}</span>
              </li>
            </ul>
            <p class="text-xs text-base-content/60">Last heard {{ d.lastHeartbeatAt | date: 'd MMM, HH:mm' }}</p>
          </div>
        </div>

        <!-- Trips on the road -->
        <div class="space-y-3">
          <h2 class="text-lg font-semibold">Trips on the road</h2>
          <nz-table
            #tripsTable
            [nzData]="trips()"
            [nzShowPagination]="trips().length > 25"
            [nzPageSize]="25"
            nzSize="small"
            [nzNoResult]="noTrips"
            [nzScroll]="{ x: '1100px' }"
          >
            <thead>
              <tr>
                <th>POS</th>
                <th>Bus</th>
                <th>Tenant</th>
                <th>Route</th>
                <th>Conductor</th>
                <th>Waiting to upload</th>
                <th>Last heard</th>
                <th>Last upload</th>
                <th>App</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let trip of tripsTable.data; trackBy: trackTrip">
                <td>
                  <span class="whitespace-nowrap" [ngClass]="view(trip.state).badge" nz-tooltip [nzTooltipTitle]="view(trip.state).hint">
                    {{ view(trip.state).label }}
                  </span>
                </td>
                <td class="font-medium">{{ trip.busName }}</td>
                <td>{{ trip.companyName }}</td>
                <td>
                  <div>{{ trip.routeName }}</div>
                  <div class="text-xs text-base-content/60">
                    {{ trip.status }} since {{ trip.onTheRoadSince | date: 'HH:mm' }}
                  </div>
                </td>
                <td>
                  <div>{{ trip.conductorName }}</div>
                  <a
                    *ngIf="trip.conductorPhoneNumber"
                    class="link link-hover text-xs"
                    [href]="telHref(trip.conductorPhoneNumber)"
                    >{{ trip.conductorPhoneNumber }}</a
                  >
                </td>
                <td>
                  <div>{{ waiting(trip.device) }}</div>
                  <div *ngIf="waitingOnline(trip.device) as online" class="text-xs text-base-content/60">
                    {{ online }}
                  </div>
                  <div
                    *ngIf="trip.device?.lastError as error"
                    class="text-xs text-error truncate max-w-64"
                    nz-tooltip
                    [nzTooltipTitle]="error"
                  >
                    {{ error }}
                  </div>
                </td>
                <td>{{ trip.device ? duration(trip.device.heartbeatAgoMs) + ' ago' : '–' }}</td>
                <td>{{ trip.device?.lastSuccessAgoMs != null ? duration(trip.device?.lastSuccessAgoMs) + ' ago' : '–' }}</td>
                <td class="text-xs text-base-content/60">
                  <div>{{ trip.device?.appVersion ?? '–' }}</div>
                  <div *ngIf="trip.device?.setAside" class="text-error">{{ trip.device?.setAside }} set aside</div>
                </td>
              </tr>
            </tbody>
          </nz-table>
          <ng-template #noTrips>
            <buspro-empty-state
              size="compact"
              icon="car"
              accent="neutral"
              title="No trips on the road right now"
              description="Trips appear here from the moment booking closes until they end."
            ></buspro-empty-state>
          </ng-template>
        </div>

        <!-- Recently resolved -->
        <div class="space-y-3">
          <h2 class="text-lg font-semibold">Resolved in the last 7 days</h2>
          <nz-table
            #recentTable
            [nzData]="recentAlerts()"
            [nzShowPagination]="recentAlerts().length > 10"
            [nzPageSize]="10"
            nzSize="small"
            [nzNoResult]="noRecent"
            [nzScroll]="{ x: '900px' }"
          >
            <thead>
              <tr>
                <th>Alert</th>
                <th>Bus</th>
                <th>Tenant</th>
                <th>What happened</th>
                <th>Opened</th>
                <th>Lasted</th>
                <th>Closed because</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let alert of recentTable.data; trackBy: trackAlert">
                <td><span [ngClass]="alertBadge(alert)">{{ alertLabel(alert) }}</span></td>
                <td class="font-medium">{{ alert.busName || '–' }}</td>
                <td>{{ alert.companyName }}</td>
                <td class="text-sm max-w-md">{{ alert.summary }}</td>
                <td>{{ alert.openedAt | date: 'd MMM, HH:mm' }}</td>
                <td>{{ lasted(alert.openedAt, alert.resolvedAt) }}</td>
                <td>{{ resolutionLabel(alert.resolution) }}</td>
              </tr>
            </tbody>
          </nz-table>
          <ng-template #noRecent>
            <buspro-empty-state
              size="compact"
              icon="check-circle"
              accent="success"
              title="No sync alerts in the last 7 days"
            ></buspro-empty-state>
          </ng-template>
        </div>

        <p class="text-xs text-base-content/60">Updated {{ o.generatedAt | date: 'HH:mm:ss' }}</p>
      </div>
    </nz-spin>
  </div>
</section>
